import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authSession } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth() || {};
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const user = await authSession(sessionId);
        setUser?.(user);
        // Successful login — strip the consumed session_id fragment so
        // reload / back-button won't try to reuse a spent token.
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user } });
      } catch (e) {
        // Give the admin/user the actual reason.  Prior fallback of
        // "Sign-in failed. Please try again." hid useful signal like
        // "Access not granted" (not invited) or "Sign-in link expired".
        console.error("Auth exchange failed", e);
        const detail = e?.response?.data?.detail;
        const status = e?.response?.status;
        const errMsg = detail
          ? (typeof detail === "string" ? detail : JSON.stringify(detail))
          : status
            ? `Sign-in failed (HTTP ${status}). Please try again from ${window.location.origin} directly, not from the email link.`
            : "Sign-in failed. Check your internet connection or open the app directly at your production URL.";
        // Clear the consumed session_id from the URL BEFORE redirecting
        // so a reload of /login doesn't re-fire the callback with the
        // stale (already-consumed) token.
        window.history.replaceState({}, "", "/login");
        navigate("/login", { replace: true, state: { authError: errMsg } });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#F9F9F8] text-[#52524E] font-mono text-sm">
      Authenticating…
    </div>
  );
}
